/* ──────────────────────────────────────────────────────────────────────────
 * Public Pricing Page — reuses the `.lpv3` design system from the main landing
 * page (LandingPageV3.css) so it is visually 1:1 with LandingPage.jsx.
 *   • 3-tier subscription ladder anchored at ₹39,999/user/IPO (unlimited changes)
 *   • Advisor Marketplace listing tiers (₹699 Standard / ₹4,999 Premium)
 *   • Global Reseller Network banner → /reseller-network
 * Tabler icons (`ti ti-*`) + Playfair/DM Sans are loaded in public/index.html.
 * ────────────────────────────────────────────────────────────────────────── */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import ContactLeadDialog from "@/components/ContactLeadDialog";
import CookieConsent from "@/components/CookieConsent";
import "./LandingPageV3.css";

const NAV_LINKS = [
  { label: "Advisors", path: "/advisors" },
  { label: "Resources", path: "/resources" },
  { label: "IPO FAQ's", path: "/ipo-faq" },
  { label: "Pricing", path: "/pricing" },
  { label: "About Us", path: "/about" },
  { label: "Careers", path: "/careers" },
  { label: "Reseller Program", path: "/reseller-network" },
];

const ADVISOR_TIERS = [
  {
    id: "adv-standard",
    name: "Standard Listing",
    tag: "Get discovered",
    price: "₹699",
    cadence: "per year",
    feats: [
      { ok: true, t: "Public profile in Advisor Marketplace" },
      { ok: true, t: "Appear in role + city search" },
      { ok: true, t: "Direct call-back leads from companies" },
      { ok: false, t: "Verified badge" },
      { ok: false, t: "Top-of-search placement" },
    ],
    cta: "List as advisor",
    to: "/payments?plan=advisor_standard",
  },
  {
    id: "adv-premium",
    name: "Premium Listing",
    tag: "Stand out & get verified",
    price: "₹4,999",
    cadence: "per year",
    popular: true,
    feats: [
      { ok: true, t: "Everything in Standard" },
      { ok: true, t: "Verified badge — SEBI / MCA / ICAI" },
      { ok: true, t: "Top-of-search placement" },
      { ok: true, t: "Premium profile + document vault" },
      { ok: true, t: "Priority matching to live IPOs" },
    ],
    cta: "Upgrade to Premium",
    to: "/payments?plan=advisor_premium",
  },
];

const FAQS = [
  {
    q: "Is the ₹39,999 plan per company or per user?",
    a: "IPO Pro is priced per user, per IPO engagement, and includes unlimited changes and re-syncs across your DRHP, Document Vault and Navigator for the entire listing journey. Add as many seats as your team needs.",
  },
  {
    q: "What do I get on the Free plan?",
    a: "The Free plan gives every founder the IPO readiness checklist, full Advisor Marketplace directory access, and basic Oracle {IPO.GPT} queries — no card required, forever.",
  },
  {
    q: "How does an Advisor Marketplace listing work?",
    a: "CAs, CS, legal, auditors, RTAs and bankers can list to get discovered by IPO-bound companies. Standard (₹699/yr) gets a searchable profile and call-back leads; Premium (₹4,999/yr) adds a verified badge and top-of-search placement.",
  },
  {
    q: "Can I get a custom enterprise plan?",
    a: "Yes — for multi-IPO rollouts, bespoke DRHP engagements or platform partnerships, talk to our team and we'll tailor pricing and onboarding to your exact needs.",
  },
];

const PriceCard = ({ tier }) => (
  <div className={`pr-card${tier.popular ? " pop" : ""}`} data-testid={`pricing-tier-${tier.id}`}>
    {tier.popular && (
      <div className="pr-pop"><i className="ti ti-sparkles" aria-hidden="true" /> Most Popular</div>
    )}
    <div className="pr-name">{tier.name}</div>
    <div className="pr-tag">{tier.tag}</div>
    <div className="pr-price">{tier.price}</div>
    <div className="pr-cadence">{tier.cadence}</div>
    <ul className="pr-feats">
      {tier.feats.map((f, i) => (
        <li key={i} className={`pr-feat${f.ok ? "" : " no"}`}>
          <i className={`ti ${f.ok ? "ti-check" : "ti-minus"}`} aria-hidden="true" />
          <span>{f.t}</span>
        </li>
      ))}
    </ul>
    <Link
      className={`pr-btn${tier.popular ? " pop" : ""}`}
      to={tier.to}
      data-testid={`pricing-cta-${tier.id}`}
    >
      {tier.cta} <i className="ti ti-arrow-right" aria-hidden="true" />
    </Link>
  </div>
);

const PricingPage = () => {
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <div className="lpv3 pr-compact" data-testid="pricing-page">
      <Seo
        title="Pricing | IPOLabs — Plans for Founders & Advisors"
        description="Transparent IPOLabs pricing — the IPO Pro platform plan at ₹39,999/user/IPO for founders, plus advisor marketplace listing tiers. Go public across NSE & BSE."
        path="/pricing"
      />
      {/* HEADER */}
      <div className="hd" data-testid="pricing-header">
        <div className="hd-inner">
          <Link to="/"><img src="/ipolabs-logo.png" alt="IPOLabs" className="brand-logo" data-testid="pricing-logo-btn" /></Link>
          <div className="nav">
            {NAV_LINKS.map((l) => (
              <Link key={l.path} to={l.path} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}>{l.label}</Link>
            ))}
          </div>
          <div className="hd-cta">
            <a className="hd-login" href="/login" target="_blank" rel="noopener noreferrer" data-testid="pricing-login-link">Login</a>
            <a className="btn-purple" href="/login" target="_blank" rel="noopener noreferrer" data-testid="pricing-get-started-btn">Get Started →</a>
          </div>
        </div>
      </div>

      {/* HERO */}
      <div className="hero" data-testid="pricing-hero">
        <span className="hero-pill">Transparent pricing · No black boxes</span>
        <div className="hero-h">Pricing built for your IPO journey</div>
        <div className="hero-h2">Start free. Scale to a full listing toolkit. Pay only for what your company needs.</div>
        <div className="pr-hero-price">₹39,999 <span>/ user / IPO — unlimited changes</span></div>
        <div className="trust">
          <span className="trust-i"><i className="ti ti-shield-check" aria-hidden="true" /> GST tax invoice</span>
          <span className="trust-i"><i className="ti ti-lock" aria-hidden="true" /> Secure Razorpay checkout</span>
          <span className="trust-i"><i className="ti ti-circle-check" aria-hidden="true" /> Cancel before access</span>
        </div>
      </div>

      {/* ADVISOR MARKETPLACE LISTING */}
      <div className="sec alt" data-testid="pricing-advisor-section">
        <div className="sec-kick">For advisors</div>
        <div className="sec-h">List on the <span className="accent">Advisor Marketplace</span></div>
        <div className="sec-d">CAs, CS, legal, auditors, RTAs and bankers — get discovered by IPO-bound companies actively building their team.</div>
        <div className="pr-grid two">
          {ADVISOR_TIERS.map((tier) => (
            <PriceCard key={tier.id} tier={tier} />
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Link className="btn-purple" to="/payments?plan=advisor_premium" data-testid="pricing-upgrade-premium-advisor">
            <i className="ti ti-crown" aria-hidden="true" /> Upgrade to Premium Advisor →
          </Link>
        </div>
      </div>

      {/* GLOBAL RESELLER NETWORK */}
      <div className="sec" data-testid="pricing-reseller-section">
        <div className="pr-reseller">
          <div className="pr-reseller-ic"><i className="ti ti-world-share" aria-hidden="true" /></div>
          <div>
            <div className="pr-reseller-h">Global Reseller Network</div>
            <div className="pr-reseller-d">
              Refer founders, firms and platforms to IPOLabs and earn up to 25% recurring revenue share. Built for CA firms, CFOs, banks and partners across India and beyond.
            </div>
          </div>
          <Link className="pr-reseller-btn" to="/reseller-network" data-testid="pricing-reseller-btn">
            Explore the network →
          </Link>
        </div>
      </div>

      {/* FAQ */}
      <div className="sec alt" data-testid="pricing-faq">
        <div className="sec-kick">FAQ</div>
        <div className="sec-h">Questions, answered</div>
        <div className="sec-d">Everything you need to know about plans, advisor listings and our reseller network.</div>
        <div className="pr-faq">
          {FAQS.map((f, i) => (
            <div key={i} className="pr-faq-item" data-testid={`faq-${i}`}>
              <div className="pr-faq-q"><i className="ti ti-help-circle" aria-hidden="true" /> {f.q}</div>
              <div className="pr-faq-a">{f.a}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="cta" data-testid="pricing-cta">
        <div className="cta-h">Ready to take your company public?</div>
        <div className="cta-d">Start free today, or talk to our team about a custom engagement for your listing.</div>
        <div className="hero-btns">
          <a className="btn-dark" href="/login" target="_blank" rel="noopener noreferrer" data-testid="pricing-cta-start-btn"><i className="ti ti-rocket" aria-hidden="true" /> Get Started Free</a>
          <button className="btn-line" onClick={() => setContactOpen(true)} data-testid="pricing-cta-talk-btn">Talk to our team</button>
        </div>
      </div>

      {/* FOOTER */}
      <div className="ft">
        <div className="ft-grid">
          <div>
            <img src="/ipolabs-logo.png" alt="IPOLabs" className="ft-brand-logo" />
            <div className="ft-tag">India&apos;s end-to-end IPO management platform — helping founders, promoters and CFOs go public with clarity and control.</div>
          </div>
          <div className="ft-col">
            <div className="ft-col-h">Platform</div>
            <Link to="/login">IPO Journey</Link>
            <Link to="/login?module=matchmaker">Advisor Marketplace</Link>
            <Link to="/login?module=drhp">DRHP Workspace</Link>
            <Link to="/login?module=funding">Funding Desk</Link>
          </div>
          <div className="ft-col">
            <div className="ft-col-h">Company</div>
            <Link to="/about">About Us</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/careers">Careers</Link>
            <Link to="/reseller-network">Reseller Program</Link>
          </div>
          <div className="ft-col">
            <div className="ft-col-h">Get started</div>
            <Link to="/login">Log in</Link>
            <Link to="/login">Create workspace</Link>
            <Link to="/advisor-marketplace">Join as advisor</Link>
            <Link to="/login">Join as investor</Link>
          </div>
          <div className="ft-col">
            <div className="ft-col-h">Legal &amp; compliance</div>
            <Link to="/disclaimer">Disclaimer</Link>
            <Link to="/resources">Resources</Link>
            <Link to="/disclaimer">Privacy Policy</Link>
            <Link to="/disclaimer">Terms of Services</Link>
          </div>
        </div>
        <div className="ft-bot">
          <span>© {new Date().getFullYear()} IPOLabs. All rights reserved.</span>
          <span>IPOLabs · India&apos;s end-to-end IPO management platform</span>
        </div>
      </div>

      <ContactLeadDialog open={contactOpen} onOpenChange={setContactOpen} leadType="sales" />
      <CookieConsent />
    </div>
  );
};

export default PricingPage;
